import { useState, useEffect, useCallback } from 'react';
import POINT_SPOT_CONSTANTS from '../constants/PointSpotConstants';
// import { getRemoteConfigValue } from '../../../services/RemoteConfigService'; // Removed unused import

// 🎯 Simple Hash Function (Seeded)
// Returns a number between 0 and 1
const pseudoRandom = (seed) => {
    let value = 0;
    for (let i = 0; i < seed.length; i++) {
        value = (value * 31 + seed.charCodeAt(i)) % 1000000007;
    }
    return value / 1000000007;
};

// Haversine distance in meters (for landmark filter)
const distanceMeters = (lat1, lng1, lat2, lng2) => {
    const R = 6371000;
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};

/**
 * usePointSpotController
 *
 * Generates temporary "Point Spots" around the user (grid) and, optionally,
 * 5 boxes per landmark within map radius. Landmark boxes are visually distinct and have a separate daily limit.
 *
 * @param {Object} center - { lat, lng } User's current center position
 * @param {Object} options - Optional. { landmarks: Array } - landmarks with id, geo: { lat, lng }
 * @returns {Array} pointSpots - Array of generated spot objects (normal + isLandmarkPointSpot)
 */
export const usePointSpotController = (center, options = {}) => {
    const [pointSpots, setPointSpots] = useState([]);
    const { landmarks = [] } = options;

    const generateSpots = useCallback(() => {
        if (!center || !center.lat || !center.lng) return [];

        const now = new Date();
        const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

        const { GRID_SIZE_DEG, RADIUS_M, PROBABILITY, LANDMARK_BOX_RADIUS_M, LANDMARK_BOX_COUNT } = POINT_SPOT_CONSTANTS.GEN;
        const spots = [];

        // --- 1) General grid-based point spots (unchanged) ---
        const gridMeters = GRID_SIZE_DEG * 111000;
        const steps = Math.ceil(RADIUS_M / gridMeters) + 1;
        const centerGridLat = Math.floor(center.lat / GRID_SIZE_DEG);
        const centerGridLng = Math.floor(center.lng / GRID_SIZE_DEG);

        for (let x = -steps; x <= steps; x++) {
            for (let y = -steps; y <= steps; y++) {
                const gridLat = centerGridLat + x;
                const gridLng = centerGridLng + y;
                const seed = `${gridLat}_${gridLng}_${todayStr}`;
                const rand = pseudoRandom(seed);

                if (rand < PROBABILITY) {
                    const offsetRandX = pseudoRandom(seed + "_x");
                    const offsetRandY = pseudoRandom(seed + "_y");
                    const spotLat = (gridLat * GRID_SIZE_DEG) + (offsetRandX * GRID_SIZE_DEG);
                    const spotLng = (gridLng * GRID_SIZE_DEG) + (offsetRandY * GRID_SIZE_DEG);

                    spots.push({
                        id: `ps_${seed}`,
                        type: 'point_spot',
                        lat: spotLat,
                        lng: spotLng,
                        title: 'Point Box',
                        isPointSpot: true,
                        seed: seed
                    });
                }
            }
        }

        // --- 2) Landmark point spots: 랜드마크당 5개, 반경 100m 안. 각도 72° 간격 + 반경 단계로 겹침 방지 ---
        if (Array.isArray(landmarks) && landmarks.length > 0) {
            const METERS_PER_DEG_LAT = 111320;
            const FIVE_ANGLES = (2 * Math.PI) / LANDMARK_BOX_COUNT; // 72° each
            landmarks.forEach((lm) => {
                const lmLat = lm.geo?.lat ?? lm.geo?.latitude;
                const lmLng = lm.geo?.lng ?? lm.geo?.longitude;
                if (lmLat == null || lmLng == null || !lm.id) return;

                const metersPerDegLng = 111320 * Math.cos((lmLat * Math.PI) / 180);
                const maxRadiusM = LANDMARK_BOX_RADIUS_M * 0.92;
                const minRadiusM = 28;

                for (let i = 0; i < LANDMARK_BOX_COUNT; i++) {
                    const seed = `lm_${lm.id}_${todayStr}_${i}`;
                    const baseAngle = i * FIVE_ANGLES;
                    const angleJitter = (pseudoRandom(seed) - 0.5) * 0.4;
                    const angleRad = baseAngle + angleJitter;
                    const tier = (maxRadiusM - minRadiusM) / LANDMARK_BOX_COUNT;
                    const distM = minRadiusM + tier * (i + 0.5) + (pseudoRandom(seed + '_d') - 0.5) * 8;
                    const clampDist = Math.max(minRadiusM, Math.min(maxRadiusM, distM));
                    const northOffsetM = clampDist * Math.cos(angleRad);
                    const eastOffsetM = clampDist * Math.sin(angleRad);
                    const dLat = northOffsetM / METERS_PER_DEG_LAT;
                    const dLng = eastOffsetM / metersPerDegLng;
                    const spotLat = lmLat + dLat;
                    const spotLng = lmLng + dLng;

                    spots.push({
                        id: seed,
                        type: 'point_spot',
                        lat: spotLat,
                        lng: spotLng,
                        title: 'Point Box',
                        isPointSpot: true,
                        isLandmarkPointSpot: true,
                        landmarkId: lm.id,
                        spotIndex: i,
                        seed
                    });
                }
            });
        }

        return spots;
    }, [center, landmarks]);

    useEffect(() => {
        const generated = generateSpots();
        setPointSpots(generated);
    }, [generateSpots]);

    return {
        pointSpots
    };
};
